import { Mixins, Component, Provide, Prop } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";
import CProdMixin from "@/mixins/cProdMixin";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { RefundOptions, RefundSources } from "@/data/enums/payments";
import { CancelOptions } from "@/data/enums/contracts";
import { CancellationRequestStatusCodes as CRSC } from "@/data/enums/contractProducts";

// Types
import { IState } from "@/store";
import {
  ContractStatusCodes as CSC,
  IContract,
  IContractProduct
} from "@/models/contracts";
import {
  ICancelOption,
  IContractCancellationMethods as ICCMethods,
  IContractCancellationProvider as ICCProvider,
  IRefundOption
} from "@/models/providers/contractCancellation";
import { IInvoice, IInvoiceProduct } from "@/models/invoices";
import _ from "@/boot/lodash";

@Component({
  computed: {
    ...mapState({
      contractCancellationScope(state: IState, getters) {
        return `${getters[`data/${DataModules.CONTRACTS}/scope`](
          this.contractId
        )}_cancel_contract`;
      },
      contract(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CONTRACTS,
          scope: this.contractCancellationScope
        });
      },
      mainInvoice(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.INVOICES,
          scope: this.mainInvoiceScope
        });
      }
    })
  }
})
export default class ContractCancellationProvider extends Mixins(
  RequestPromises,
  CProdMixin,
  Context
) {
  // Props
  @Prop({ default: null })
  public contractProductId!: ICCProvider["contractProductId"];

  @Prop({ required: true })
  public contractId!: ICCProvider["contractId"];

  @Prop({ default: () => [] })
  public productIds!: IContractProduct["id"][];

  // Data
  public approveRequest: boolean | null = null;
  public contract!: IContract;
  public contractCancellationScope!: string;
  public hasRequestError = false;
  public isLoading = false;
  public isProcessing = false;
  public mainInvoice!: IInvoice;
  public mainInvoiceScope = "contract_cancellation_main_invoice_scope";
  public cancelOption: ICCProvider["cancelOption"] = null;
  public refundOption: ICCProvider["refundOption"] = null;
  public refundSource: ICCProvider["refundSource"] =
    RefundSources.ORIGINAL_SOURCE;
  public cancellationReason = "";

  public async created() {
    await this.loadData(true);
    if (this.isPending) {
      await this.loadMainInvoice();
    }
    this.isLoading = false;
  }

  @Provide()
  public get contractCancellationMethods(): ICCMethods {
    return {
      selectCancelOption: this.selectCancelOption,
      selectRefundOption: this.selectRefundOption,
      selectRefundSource: this.selectRefundSource
    };
  }

  @Provide()
  public contractCancellationProvider(): ICCProvider {
    return {
      cancelOption: this.cancelOption,
      cancelOptions: this.cancelOptions,
      contract: this.contract,
      contractId: this.contractId,
      contractProduct: this.contractProduct,
      contractProductId: this.contractProductId,
      daysLeft: this.daysLeft,
      isInMoneyBackPeriod: this.isInMoneyBackPeriod,
      isLoading: this.isLoading,
      isProcessing: this.isProcessing,
      moneyBackPeriod: this.moneyBackPeriod,
      refundOption: this.refundOption,
      refundOptions: this.refundOptions,
      refundSource: this.refundSource,
      showMoneyBackMsg: this.showMoneyBackMsg,
      showRefundOptions: this.showRefundOptions
    };
  }

  public get cancelOptions(): ICCProvider["cancelOptions"] {
    return _.map(
      _.filter(
        this.isAdmin ? this.adminCancelOptions : this.clientCancelOptions,
        option => option.if
      ),
      option => _.pick(option, ["id", "title", "onSubmit"])
    ) as ICCProvider["cancelOptions"];
  }

  public get adminCancelOptions(): ICCProvider["cancelOptions"] {
    return [
      {
        id: CancelOptions.DONT_CANCEL,
        title: "_.dont_cancel",
        if: this.isProductCancellation && this.hasAutoExpireEnabled,
        onSubmit: () => {
          return this.toggleAutoExpire(false);
        }
      },
      {
        id: CancelOptions.CANCEL_WITH_IMMEDIATE_EFFECT,
        title: this.isAdmin
          ? "_sentence.cancel_with_immediate_effect"
          : "_sentence.request_immediate_cancellation",
        if: this.canCancelAnytime,
        onSubmit: () => {
          return this.$store.dispatch(
            `data/${
              this.isProductCancellation
                ? DataModules.CONTRACTS_PRODUCTS
                : DataModules.CONTRACTS
            }/cancel`,
            {
              data: this.showRefundOptions ? this.refundPayload : null,
              contractId: this.contractId,
              ...(this.isProductCancellation
                ? { contractProductId: this.contractProductId }
                : {})
            }
          );
        }
      },
      {
        id: CancelOptions.CANCEL_AFTER_CURRENT_TERM,
        title: this.isProductCancellation
          ? "_sentence.cancel_at_the_end_of_the_billing_term_with_billing_term"
          : "_sentence.cancel_at_the_end_of_the_billing_term",
        if: this.isProductCancellation && !this.isPending,
        onSubmit: () => {
          return this.toggleAutoExpire(true);
        }
      },
      {
        id: CancelOptions.CANCEL_ON_A_SPECIFIC_DATE,
        title: "_sentence.cancel_on_a_specific_date",
        if: false /* Not currently supported on BE */,
        onSubmit: () => Promise.resolve()
      }
    ];
  }

  public get clientCancelOptions(): ICCProvider["cancelOptions"] {
    return [
      {
        id: CancelOptions.DONT_CANCEL,
        title: "_.dont_cancel",
        if: this.isProductCancellation && this.hasAutoExpireEnabled,
        onSubmit: () => {
          return this.toggleAutoExpire(false);
        }
      },
      {
        id: CancelOptions.CANCEL_AFTER_CURRENT_TERM,
        title: this.isProductCancellation
          ? "_sentence.cancel_at_the_end_of_the_billing_term_with_billing_term"
          : "_sentence.cancel_at_the_end_of_the_billing_term",
        if: this.isProductCancellation && !this.isPending,
        onSubmit: () => {
          return this.toggleAutoExpire(true);
        }
      },
      {
        id: CancelOptions.CANCEL_WITH_IMMEDIATE_EFFECT,
        title: this.isAdmin
          ? "_sentence.cancel_with_immediate_effect"
          : "_sentence.request_immediate_cancellation",
        if: !this.hasActiveCancellationRequest && this.canCancelAnytime,
        onSubmit: () => {
          return this.requestCancellation();
        }
      }
    ];
  }

  public get cancellationRequest() {
    return this.contract?.cancellation_request;
  }

  public get hasActiveCancellationRequest() {
    return (
      this.cancellationRequest?.status?.code ===
        CRSC.CANCELLATION_REQUEST_REQUESTED &&
      (_.isEmpty(this.cancellationRequest?.product_ids) ||
        (this.cancellationRequest?.product_ids || []).includes(
          this.contractProduct.id
        ))
    );
  }

  public toggleAutoExpire(expire: boolean) {
    return this.$store.dispatch(
      `data/${DataModules.CONTRACTS_PRODUCTS}/modify_renew`,
      {
        contractId: this.contractId,
        contractProductId: this.contractProductId,
        payload: {
          renew: !expire
        }
      }
    );
  }

  public requestCancellation() {
    return this.$store.dispatch(
      `data/${DataModules.CONTRACTS}/requestCancellation`,
      {
        id: this.contractId,
        data: {
          cancellation_reason: this.cancellationReason,
          product_ids: this.productIds
        }
      }
    );
  }

  public get moneyBackPeriod() {
    return this.$_.get(
      this.contractProduct,
      "recommended_money_back_period",
      0
    );
  }

  public get isInMoneyBackPeriod() {
    // Return `false` if NOT in a product context
    if (!this.isProductCancellation) return false;
    const activationDate = this.contractProduct?.activation_date;
    if (!activationDate || !this.moneyBackPeriod) return false;
    const moneyBackEndMoment = this.$moment(new Date(activationDate)).add(
      this.moneyBackPeriod,
      "days"
    );
    return moneyBackEndMoment > this.$moment();
  }

  public get showMoneyBackMsg() {
    if (!this.isProductCancellation) return false;
    return (
      ![
        CSC.PENDING,
        CSC.AWAITING_ACTIVATION,
        CSC.CANCELLED,
        CSC.CLOSED
      ].includes(this.contract?.status?.code as CSC) && !!this.moneyBackPeriod
    );
  }

  public get hasAutoExpireEnabled() {
    return this.$store.getters[
      `data/${DataModules.CONTRACTS_PRODUCTS}/hasAutoExpireEnabled`
    ](this.contractProduct);
  }

  public get isSubscription() {
    return this.contractProduct.billing_cycle_months > 0;
  }

  public get canCancelAnytime() {
    return !_.some(
      this.isProductCancellation
        ? [this.contractProduct]
        : [...this.contract?.products],
      ["can_cancel", false]
    );
  }

  public get refundOptions(): ICCProvider["refundOptions"] {
    const options = [
      {
        id: RefundOptions.PARTIAL_REFUND,
        title: "_sentence.issue_pro_rata_refund_for_unused_term",
        if: !this.isPending
      },
      {
        id: RefundOptions.INITIAL_PARTIAL_PAYMENT,
        title: "_sentence.issue_refund_for_initial_partial_payment",
        if: this.isPending
      },
      {
        id: RefundOptions.FULL_REFUND,
        title: "_sentence.issue_full_refund_for_current_term",
        if: !this.isPending
      },
      {
        id: RefundOptions.NO_REFUND,
        title: "_sentence.issue_refund_manually",
        if: true
      }
    ];
    return _.map(
      _.filter(options, option => option.if),
      option => _.pick(option, ["id", "title"])
    );
  }

  public get immediatelyWithFullRefund() {
    return (
      this.refundOption === RefundOptions.FULL_REFUND &&
      this.cancelOption === CancelOptions.CANCEL_WITH_IMMEDIATE_EFFECT
    );
  }

  public get showRefundOptions() {
    if (this.isCancellationRequest && !this.approveRequest) return false;
    if (this.cancelOption === CancelOptions.DONT_CANCEL) return false;
    if (this.cancelOption === CancelOptions.CANCEL_AFTER_CURRENT_TERM)
      return false;
    if (this.isPending) {
      if (this.isProductCancellation) {
        if (!this.mainInvoice) return false;
        const invoiceProducts: IInvoiceProduct[] = _.get(
          this.mainInvoice,
          "products",
          []
        );
        const invoiceProduct = invoiceProducts.find(
          ip => ip.contracts_product_id === this.contractProductId
        ) as IInvoiceProduct;

        return (
          _.get(invoiceProduct, "configuration_total_amount_converted", 0) >
            _.get(this.mainInvoice, "unpaid_amount_converted", 0) &&
          _.get(this.mainInvoice, "paid_amount_converted", 0) > 0
        );
      } else {
        return _.get(this.mainInvoice, "paid_amount_converted", 0) > 0;
      }
    }
    return this.daysLeft > 0;
  }

  public get refundPayload() {
    return {
      refund_mode: this.refundOption || RefundOptions.NO_REFUND,
      refund_source: this.refundOption ? this.refundSource : undefined
    };
  }

  public get isProductCancellation(): boolean {
    return !!this.contractProductId;
  }

  public get daysLeft(): ICCProvider["daysLeft"] {
    const nextDueDate = _.get(
      this.isProductCancellation ? this.contractProduct : this.contract,
      "next_due_date",
      "0000-00-00"
    );
    return this.$moment(nextDueDate !== "0000-00-00" ? nextDueDate : undefined)
      .startOf("day")
      .diff(this.$moment().startOf("day"), "days");
  }

  public get isPending(): boolean {
    return this.contract?.status?.code === CSC.PENDING;
  }

  public get isCancellationRequest() {
    return !_.isEmpty(this.cancellationRequest);
  }

  public get hasPaidFutureInvoices() {
    return _.some(_.get(this.contract, "products", []), product => {
      return _.get(product, "paid_future_invoices", false);
    });
  }

  public get contractProduct(): IContractProduct {
    return (
      _.find(_.get(this.contract, "products", []), [
        "id",
        this.contractProductId
      ]) || ({} as IContractProduct)
    );
  }

  public async loadData(ignoreStored = false) {
    try {
      this.isLoading = true;
      await this.getContractData(ignoreStored);
      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
    }
  }

  public async getContractData(ignoreStored) {
    return this.$store.dispatch(`data/${DataModules.CONTRACTS}/get`, {
      id: this.contractId,
      scope: this.contractCancellationScope,
      params: {
        with: [
          "products.product.image",
          "cancellation_request",
          "products.status",
          "client.image",
          "status"
        ].join(",")
      },
      vm: this,
      ignoreStored
    });
  }

  public async loadMainInvoice() {
    return this.$store.dispatch(`data/${DataModules.INVOICES}/get`, {
      id: this.contract.main_invoice_id,
      params: {
        with: "products"
      },
      scope: this.mainInvoiceScope,
      vm: this
    });
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.CONTRACTS,
      scope: this.contractCancellationScope,
      vm: this
    });

    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.INVOICES,
      scope: this.mainInvoiceScope,
      vm: this
    });
  }

  public selectCancelOption(option: ICancelOption) {
    this.$set(this, "cancelOption", _.get(option, "id", null));
  }

  public selectRefundOption(option: IRefundOption) {
    this.$set(this, "refundOption", _.get(option, "id", null));
  }

  public selectRefundSource(source: RefundSources) {
    this.$set(this, "refundSource", source);
  }

  public submitCancellationRequest() {
    return this.$store.dispatch(
      `data/${DataModules.CONTRACTS}/${
        this.approveRequest ? "approveCancellation" : "rejectCancellation"
      }`,
      {
        id: this.contractId,
        data: {
          contract_request_id: this.cancellationRequest?.id,
          ...(this.approveRequest ? this.refundPayload : {})
        }
      }
    );
  }
}
